import {
    COMPLETION_SECTION_NAME,
    PORTAL_SECTION_NAME,
    QUALITY_SECTION_NAME,
} from './customer-claim-inspection-utils'

export const CLAIM_WORKFLOW_PHASE_LABELS = {
    1: PORTAL_SECTION_NAME,
    2: QUALITY_SECTION_NAME,
    3: COMPLETION_SECTION_NAME,
}

export const extractWorkflowErrorMessage = err => {
    const payload = err?.error
    if (typeof payload === 'string' && payload.trim()) {
        return payload.trim()
    }
    if (payload && typeof payload === 'object') {
        const nested =
            payload.Message ||
            payload.message ||
            payload.ErrorMessage ||
            payload.errorMessage
        if (nested && String(nested).trim()) {
            return String(nested).trim()
        }
    }
    if (err?.status === 403) {
        return 'Super Admin only. Section workflow configuration affects every audit on the template.'
    }
    return err?.message || 'Request failed. Please try again.'
}

export const USER_SEARCH_DEFAULT_LIMIT = 25
export const USER_SEARCH_MAX_LIMIT = 200

export const unwrapWorkflowResponse = response => {
    if (Array.isArray(response)) return response
    if (Array.isArray(response?.Assignments)) return response.Assignments
    if (Array.isArray(response?.assignments)) return response.assignments
    if (Array.isArray(response?.Data)) return response.Data
    if (Array.isArray(response?.data)) return response.data
    return []
}

export const unwrapRecipientsResponse = response => {
    if (Array.isArray(response)) return response
    if (Array.isArray(response?.Recipients)) return response.Recipients
    if (Array.isArray(response?.recipients)) return response.recipients
    if (Array.isArray(response?.Data)) return response.Data
    if (Array.isArray(response?.data)) return response.data
    return []
}

export const unwrapUserSearchResponse = response => {
    if (Array.isArray(response)) return response
    if (Array.isArray(response?.Users)) return response.Users
    if (Array.isArray(response?.users)) return response.users
    if (Array.isArray(response?.Data)) return response.Data
    if (Array.isArray(response?.data)) return response.data
    return []
}

const parseAssignedUserIds = row => {
    const users = row?.AssignedUsers ?? row?.assignedUsers
    if (Array.isArray(users) && users.length) {
        return users
            .map(user =>
                Number(
                    user?.UserId ??
                        user?.userId ??
                        user?.Id ??
                        user?.id,
                ),
            )
            .filter(id => Number.isFinite(id) && id > 0)
    }

    const multi = row?.AssignedUserIds ?? row?.assignedUserIds
    if (Array.isArray(multi) && multi.length) {
        return multi
            .map(id => Number(id))
            .filter(id => Number.isFinite(id) && id > 0)
    }

    // Legacy QA rows may only have AssignedUserId until allowlist is populated.
    const single = row?.AssignedUserId ?? row?.assignedUserId
    if (single != null && single !== '') {
        const id = Number(single)
        return Number.isFinite(id) && id > 0 ? [id] : []
    }
    return []
}

const normalizeAssignedUsersFromRow = (row, assignedUserIds) => {
    const users = row?.AssignedUsers ?? row?.assignedUsers
    if (Array.isArray(users) && users.length) {
        return users
            .map(user => {
                const id = Number(
                    user?.UserId ??
                        user?.userId ??
                        user?.Id ??
                        user?.id,
                )
                if (!Number.isFinite(id)) return null
                return {
                    id,
                    fullName: String(
                        user?.FullName ??
                            user?.fullName ??
                            user?.Name ??
                            user?.name ??
                            '',
                    ).trim(),
                    email: String(user?.Email ?? user?.email ?? '').trim(),
                }
            })
            .filter(user => user?.id)
    }

    const singleId = assignedUserIds[0]
    const fullName = String(
        row?.AssignedUserName ??
            row?.assignedUserName ??
            row?.AssignedUserFullName ??
            row?.assignedUserFullName ??
            '',
    ).trim()
    const email = String(
        row?.AssignedUserEmail ?? row?.assignedUserEmail ?? '',
    ).trim()
    if (singleId && (fullName || email)) {
        return [{ id: singleId, fullName, email }]
    }
    return []
}

export const normalizeWorkflowAssignment = row => {
    if (!row || typeof row !== 'object') return null
    const phaseOrder = Number(row?.PhaseOrder ?? row?.phaseOrder ?? 0)
    const sectionId = String(row?.SectionId ?? row?.sectionId ?? '').trim()
    if (!sectionId || !phaseOrder) return null
    const assignedUserIds = parseAssignedUserIds(row)
    return {
        id: row?.Id ?? row?.id ?? null,
        sectionId,
        phaseOrder,
        assignedUserIds,
        assignedUsers: normalizeAssignedUsersFromRow(row, assignedUserIds),
        completeLabel: String(
            row?.CompleteLabel ?? row?.completeLabel ?? '',
        ).trim(),
        waitingLabel: String(
            row?.WaitingLabel ?? row?.waitingLabel ?? '',
        ).trim(),
        emailSubject: String(
            row?.EmailSubject ?? row?.emailSubject ?? '',
        ).trim(),
        emailBodyKey: String(
            row?.EmailBodyKey ?? row?.emailBodyKey ?? '',
        ).trim(),
        sectionName:
            CLAIM_WORKFLOW_PHASE_LABELS[phaseOrder] ||
            `Phase ${phaseOrder}`,
    }
}

export const normalizeWorkflowAssignments = rows =>
    (Array.isArray(rows) ? rows : [])
        .map(normalizeWorkflowAssignment)
        .filter(Boolean)
        .sort((a, b) => a.phaseOrder - b.phaseOrder)

export const normalizeNotificationRecipient = row => {
    if (!row || typeof row !== 'object') return null
    const internalUserId =
        row?.InternalUserId ?? row?.internalUserId ?? null
    const externalEmail = String(
        row?.ExternalEmail ?? row?.externalEmail ?? '',
    ).trim()
    if (!internalUserId && !externalEmail) return null
    return {
        id: row?.Id ?? row?.id ?? null,
        internalUserId: internalUserId ? Number(internalUserId) : null,
        internalUserName: String(
            row?.InternalUserName ?? row?.internalUserName ?? '',
        ).trim(),
        internalUserEmail: String(
            row?.InternalUserEmail ?? row?.internalUserEmail ?? '',
        ).trim(),
        externalEmail,
        externalName: String(
            row?.ExternalName ?? row?.externalName ?? '',
        ).trim(),
    }
}

export const normalizeNotificationRecipients = rows =>
    (Array.isArray(rows) ? rows : [])
        .map(normalizeNotificationRecipient)
        .filter(Boolean)

export const toNotificationRecipientPayload = recipient => {
    if (recipient?.internalUserId) {
        return {
            InternalUserId: Number(recipient.internalUserId),
        }
    }
    const externalEmail = String(recipient?.externalEmail ?? '').trim()
    const externalName = String(recipient?.externalName ?? '').trim()
    const payload = { ExternalEmail: externalEmail }
    if (externalName) {
        payload.ExternalName = externalName
    }
    return payload
}

export const toWorkflowAssignmentPayload = phase => {
    const payload = {
        SectionId: phase.sectionId,
        PhaseOrder: Number(phase.phaseOrder),
        AssignedUserIds: (phase.assignedUserIds || [])
            .map(id => Number(id))
            .filter(id => Number.isFinite(id) && id > 0),
        CompleteLabel: phase.completeLabel ?? '',
        WaitingLabel: phase.waitingLabel?.trim()
            ? phase.waitingLabel.trim()
            : null,
        EmailSubject: phase.emailSubject ?? '',
    }
    if (phase.emailBodyKey?.trim()) {
        payload.EmailBodyKey = phase.emailBodyKey.trim()
    }
    return payload
}

export const validatePhaseSetup = phases => {
    const rows = Array.isArray(phases) ? phases : []
    const orders = rows
        .map(phase => Number(phase.phaseOrder))
        .filter(order => Number.isFinite(order) && order > 0)
        .sort((a, b) => a - b)

    const denseGap = orders.findIndex((order, index) => order !== index + 1)
    if (denseGap !== -1) {
        return 'PhaseOrder values must be dense starting at 1 (no gaps).'
    }

    const missingUser = rows.find(
        phase => !(phase.assignedUserIds || []).length,
    )
    if (missingUser) {
        return `Phase ${missingUser.phaseOrder} needs at least one assigned user.`
    }

    return null
}

export const isDuplicateRecipient = (list, candidate) => {
    if (!candidate) return false
    return (list || []).some(existing => {
        if (candidate.internalUserId && existing.internalUserId) {
            return (
                Number(existing.internalUserId) ===
                Number(candidate.internalUserId)
            )
        }
        if (candidate.externalEmail && existing.externalEmail) {
            return (
                String(existing.externalEmail).trim().toLowerCase() ===
                String(candidate.externalEmail).trim().toLowerCase()
            )
        }
        return false
    })
}

export const normalizeUserSearchResult = row => {
    if (!row || typeof row !== 'object') return null
    const id = row?.Id ?? row?.id
    if (id == null) return null
    return {
        id: Number(id),
        fullName: String(row?.FullName ?? row?.fullName ?? '').trim(),
        email: String(row?.Email ?? row?.email ?? '').trim(),
        roleId: Number(row?.RoleId ?? row?.roleId ?? 0) || null,
        isActive: row?.IsActive ?? row?.isActive ?? true,
    }
}

export const normalizeUserSearchResults = rows =>
    (Array.isArray(rows) ? rows : [])
        .map(normalizeUserSearchResult)
        .filter(Boolean)

export const collectAssignedUserIds = phases =>
    [
        ...new Set(
            (phases || []).flatMap(phase => phase?.assignedUserIds || []),
        ),
    ].map(id => Number(id))

export const companyUsersFromAssignments = assignments =>
    (assignments || []).flatMap(phase =>
        (phase?.assignedUsers || []).filter(
            user => user?.id && (user.fullName || user.email),
        ),
    )

export const dedupeCompanyUsers = users => {
    const map = new Map();
    (users || []).forEach(user => {
        if (!user?.id) return
        const prev = map.get(user.id)
        map.set(user.id, {
            id: user.id,
            fullName: user.fullName || prev?.fullName || '',
            email: user.email || prev?.email || '',
            roleId: user.roleId ?? prev?.roleId ?? null,
            isActive: user.isActive ?? prev?.isActive ?? true,
        })
    })
    return Array.from(map.values()).filter(
        user => user.fullName || user.email,
    )
}

export const companyUsersFromRecipientsMap = recipientsMap => {
    const users = []
    Object.values(recipientsMap || {}).forEach(rows => {
        (rows || []).forEach(row => {
            if (!row?.internalUserId) return
            if (!row.internalUserName && !row.internalUserEmail) return
            users.push({
                id: row.internalUserId,
                fullName: row.internalUserName || '',
                email: row.internalUserEmail || '',
            })
        })
    })
    return users
}
